import React, { type ComponentProps, type ReactNode } from 'react';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Pressable } from '@atlaskit/primitives/compiled';

import type { PopupTriggerWithHover } from '../../popup-adapter/PopupTriggerWithHover';

const styles = cssMap({
	trigger: {
		all: 'unset',
		display: 'inline',
	},
});

type TeamProfileCardTriggerProps = {
	trigger?: ComponentProps<typeof PopupTriggerWithHover>['triggerMode'];
	triggerProps: TriggerProps;
	children: ReactNode;
};

export const TeamProfileCardTrigger = ({
	trigger,
	triggerProps,
	children,
}: TeamProfileCardTriggerProps): React.JSX.Element => {
	const isNonInteractiveHoverTriggerEnabled = fg('townsquare_a11y_team_profilecard');

	const hoverOnlyTrigger = Array.isArray(trigger)
		? trigger.every((mode) => mode === 'hover')
		: trigger === 'hover';
	const useNonInteractiveHoverTrigger = isNonInteractiveHoverTriggerEnabled && hoverOnlyTrigger;

	if (useNonInteractiveHoverTrigger) {
		const { 'aria-expanded': _ariaExpanded, ...nonDialogTriggerProps } = triggerProps;

		return (
			<Box
				as="span"
				{...nonDialogTriggerProps}
				testId="team-profilecard-trigger-wrapper"
				xcss={styles.trigger}
			>
				{children}
			</Box>
		);
	}

	if (fg('platform_create_work_item_modernization')) {
		// Avoid invalid HTML: <span> wrapper prevents <button> from wrapping interactive children like <a>.
		// onKeyDownCapture intercepts Enter before popup fires, delegating to the inner <a> link instead.
		const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLSpanElement>) => {
			if (e.key === 'Enter' || e.key === ' ') {
				const link = e.currentTarget.querySelector<HTMLAnchorElement>('a[href]');
				if (link) {
					e.preventDefault();
					e.stopPropagation();
					link.click();
				}
			}
		};

		// Strip aria-expanded: a plain <span> has no role, so aria-expanded would be invalid HTML.
		const { 'aria-expanded': _ariaExpanded, ...spanTriggerProps } = triggerProps;

		return (
			<span onKeyDownCapture={handleKeyDownCapture}>
				<Box
					as="span"
					{...spanTriggerProps}
					testId="team-profilecard-trigger-wrapper"
					xcss={styles.trigger}
				>
					{children}
				</Box>
			</span>
		);
	}

	return (
		<Pressable {...triggerProps} testId="team-profilecard-trigger-wrapper" xcss={styles.trigger}>
			{children}
		</Pressable>
	);
};
